import {
  broadcastSync,
  type CosmosSignData,
  CosmosSigner,
  type CosmosSignMessageOptions,
  categorize,
  computeCosmosTxHashHex,
  estimateFee,
  extractSyncResponseTxHashHex,
  fetchCosmosGasPrice,
  getSigningStargateClient,
  type ICosmosSigner,
  isDuplicateBroadcastError,
  sign,
} from '@polaris/core/cosmos'
import { debug } from '@polaris/core/utils'
import { useChain, useConnect, useQuirks } from '@quirks/react'
import { signArbitrary } from '@quirks/store'
import { base64, bech32 } from '@scure/base'
import { useAtomValue } from 'jotai'
import { useCallback, useMemo } from 'react'
import { cosmosDerivationPathsAtom } from '@/src/stores/hotkeys/atoms'
import { wrapCatchCategorize } from '@/src/utils/error'

export const useCosmosWalletSigner = (defaultChainName = 'osmosis') => {
  const { address: defaultAddress, chain: defaultChain } =
    useChain(defaultChainName)
  const { wallet, walletName } = useConnect()
  const store = useQuirks()
  const derivationPaths = useAtomValue(cosmosDerivationPathsAtom)
  const accounts = store.use.accounts()

  const getPublicKey = useCallback(
    (chainId?: string): string | null => {
      const pubKeyBytes = accounts.find(
        (account) => account.chainId === (chainId ?? 'osmosis-1'),
      )?.pubKey

      return pubKeyBytes ? base64.encode(pubKeyBytes) : null
    },
    [accounts],
  )

  const deriveAddress = useCallback(
    (chainId?: string): string | undefined => {
      /*
       * If the user wallet is undefined, it can happens with some wallets like leap,
       * when it's unlocked sometimes it get in an infinite state and it need to ask a second
       * time to approve the connection, this check prevent the user to swap or use that account
       * since it's not a valid connection
       */
      if (!defaultAddress || !wallet) {
        return undefined
      }

      const derivationPath =
        chainId && derivationPaths
          ? (derivationPaths[chainId.split(':').pop()!] ??
            CosmosSigner.DEFAULT_COSMOS_CHAIN_METADATA)
          : CosmosSigner.DEFAULT_COSMOS_CHAIN_METADATA

      const pubkey = bech32.decode(defaultAddress as `${string}1${string}`)

      const derivedAddress = bech32.encode(
        derivationPath?.prefix ?? pubkey.prefix,
        pubkey.words,
      )

      return derivedAddress
    },
    [defaultAddress, derivationPaths, wallet],
  )

  const estimateFees = useCallback(
    async (options: CosmosSignData) => {
      debug('estimateFees: ', accounts, store.getState())

      // Get signing client
      const client = await getSigningStargateClient(
        store,
        options.chain,
        'auto',
      )

      // Fetch gas price using centralized logic
      const { gasPrice, fetchedAt } = await fetchCosmosGasPrice(
        options.chain,
        options.chain.chain_id,
      )

      const { accountNumber, sequence } = await client.getSequence(
        options.sender,
      )
      debug(
        `Get sequence - Estimating fees - Sender: ${options.sender} - AccountNumber: ${accountNumber} - Sequence: ${sequence}`,
      )

      const explicitSignerData = {
        chainId: options.chain.chain_id,
        accountNumber,
        sequence,
      }

      const fee = await estimateFee(
        client,
        options.sender,
        options.msgs,
        options.chain,
        gasPrice,
        options.memo,
        explicitSignerData,
      )

      return {
        gasPrice,
        fee,
        explicitSignerData,
        gasPriceFetchedAt: fetchedAt,
      }
    },
    [store, accounts],
  )

  const signAndBroadcast = useCallback(
    async (options: CosmosSignData) => {
      const { getAssetsByChainName } = await import('@/src/utils/chains')
      const assetList = getAssetsByChainName(options.chain.chain_name)
      await wallet?.suggestChains([
        {
          name: options.chain.chain_name,
          chain: options.chain,
          assetList: assetList!,
        },
      ])

      // Log wallet information
      debug(`cosmos signAndBroadcast with wallet ${walletName}`)

      const signResult = await sign(
        store,
        options.chain,
        options.msgs,
        options?.fee ?? 'auto',
        'auto',
        options.memo,
        options.explicitSignerData,
        undefined,
        options.sender,
        options.gasPriceFetchedAt, // Pass timestamp for staleness checking
      )

      try {
        const response = await broadcastSync(options.chain, signResult)
        const responseTxHash = extractSyncResponseTxHashHex(response)
        if (responseTxHash) {
          return responseTxHash as unknown as string
        }
        // Fallback: compute locally if RPC response lacks hash
        return (await computeCosmosTxHashHex(signResult)) as unknown as string
      } catch (error) {
        if (isDuplicateBroadcastError(error)) {
          // Compute the tx hash only in the duplicate-broadcast error path
          const computedTxHash = await computeCosmosTxHashHex(signResult)

          debug(
            'Cosmos Wallet Signer: duplicate tx detected (already in cache/mempool). Treating as success.',
            {
              chainId: options.chain.chain_id,
              sender: options.sender,
              computedTxHash,
            },
          )
          return computedTxHash
        }

        throw error
      }
    },
    [wallet, store, walletName],
  )

  const signMessage = useCallback(
    (options: CosmosSignMessageOptions) =>
      signArbitrary(
        store,
        defaultChain.chain_id,
        defaultAddress!,
        options.data,
      ).then(({ signature }) => signature),
    [defaultAddress, defaultChain.chain_id, store],
  )

  return useMemo(
    () => ({
      deriveAddress: wrapCatchCategorize(deriveAddress, categorize),
      estimateFees: wrapCatchCategorize(estimateFees, categorize),
      signAndBroadcast: wrapCatchCategorize(signAndBroadcast, categorize),
      signMessage: wrapCatchCategorize(signMessage, categorize),
      getPublicKey: wrapCatchCategorize(getPublicKey, categorize),
    }),
    [deriveAddress, estimateFees, signAndBroadcast, signMessage, getPublicKey],
  ) satisfies ICosmosSigner
}
